/**
 * Structured Data Component
 * 
 * Provides JSON-LD structured data for various schema types
 * to help search engines understand content and enable rich snippets.
 */

import React from 'react';

export interface OrganizationSchema {
  name: string;
  logo?: string;
  url: string;
  contactPoint?: {
    telephone: string;
    contactType: string;
    areaServed: string;
  };
  sameAs?: string[];
}

export interface LocalBusinessSchema {
  '@type': 'LocalBusiness';
  name: string;
  image?: string;
  '@id': string;
  url: string;
  telephone?: string;
  address?: {
    '@type': 'PostalAddress';
    streetAddress?: string;
    addressLocality?: string;
    addressRegion?: string;
    postalCode?: string;
    addressCountry?: string;
  };
  geo?: {
    '@type': 'GeoCoordinates';
    latitude?: number;
    longitude?: number;
  };
  openingHoursSpecification?: Array<{
    '@type': 'OpeningHoursSpecification';
    dayOfWeek: string[];
    opens: string;
    closes: string;
  }>;
}

export interface ProductSchema {
  '@type': 'Product';
  name: string;
  description?: string;
  image?: string | string[];
  brand?: {
    '@type': 'Brand';
    name: string;
  };
  category?: string;
  offers?: {
    '@type': 'Offer';
    availability?: string;
    price?: string;
    priceCurrency?: string;
  };
  aggregateRating?: {
    '@type': 'AggregateRating';
    ratingValue: string;
    reviewCount: string;
  };
}

export interface BreadcrumbSchema {
  '@type': 'BreadcrumbList';
  itemListElement: Array<{
    '@type': 'ListItem';
    position: number;
    name: string;
    item?: string;
  }>;
}

export interface WebSiteSchema {
  '@type': 'WebSite';
  name: string;
  url: string;
  potentialAction?: {
    '@type': 'SearchAction';
    target: {
      '@type': 'EntryPoint';
      urlTemplate: string;
    };
    'query-input': string;
  };
}

export interface ArticleSchema {
  '@type': 'Article';
  headline: string;
  description?: string;
  image?: string | string[];
  datePublished?: string;
  dateModified?: string;
  author?: {
    '@type': 'Organization';
    name: string;
  };
  publisher?: {
    '@type': 'Organization';
    name: string;
    logo?: {
      '@type': 'ImageObject';
      url: string;
    };
  };
}

interface StructuredDataProps {
  data:
    | OrganizationSchema
    | LocalBusinessSchema
    | ProductSchema
    | BreadcrumbSchema
    | WebSiteSchema
    | ArticleSchema
    | Record<string, any>;
  type?: string;
}

/**
 * StructuredData Component
 * 
 * Renders JSON-LD structured data script tag for SEO
 */
export default function StructuredData({ data, type }: StructuredDataProps) {
  const schema = {
    '@context': 'https://schema.org',
    ...data,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema, null, 0) }}
    />
  );
}

/**
 * Organization Schema Helper
 */
export function Organization({ name, logo, url, contactPoint, sameAs }: OrganizationSchema) {
  const schema: any = {
    '@type': 'Organization',
    name,
    url,
  };

  if (logo) schema.logo = logo;
  if (contactPoint) schema.contactPoint = contactPoint;
  if (sameAs && sameAs.length > 0) schema.sameAs = sameAs;

  return <StructuredData data={schema} />;
}

/**
 * LocalBusiness Schema Helper
 */
export function LocalBusiness(props: Omit<LocalBusinessSchema, '@type' | '@id'> & { id?: string }) {
  const data: LocalBusinessSchema = {
    '@type': 'LocalBusiness',
    '@id': props.id || props.url,
    ...props,
  };
  return <StructuredData data={data} />;
}

/**
 * Product Schema Helper
 */
export function Product(props: Omit<ProductSchema, '@type'>) {
  return <StructuredData data={{ '@type': 'Product', ...props }} />;
}

/**
 * Breadcrumb Schema Helper
 */
export function Breadcrumb(data: BreadcrumbSchema) {
  return <StructuredData data={data} />;
}

/**
 * WebSite Schema Helper
 */
export function WebSite(data: WebSiteSchema) {
  return <StructuredData data={data} />;
}

/**
 * Article Schema Helper
 */
export function Article(data: ArticleSchema) {
  return <StructuredData data={data} />;
}